import { appsyncPublicGraphqlRequest } from './appsyncPublicGraphql';

/** AppSync returns this when the deployed schema is older than the app (fields not yet on TherapistProfile). */
export function isStaleTherapistProfileSchemaError(message: string): boolean {
  return (
    message.includes('FieldUndefined') &&
    (message.includes('profilePhotoKey') || message.includes('sessionRateDisplay')) &&
    message.includes('TherapistProfile')
  );
}

/**
 * Deployed GraphQL input types are older than `amplify/data/resource.ts` (e.g. no profilePhotoKey).
 * Example: "The variables input contains a field that is not defined for input object type 'UpdateTherapistProfileInput'"
 */
export function isUnsupportedTherapistProfileMutationInputError(message: string): boolean {
  const m = message.toLowerCase();
  const mentionsInput =
    m.includes('updatetherapistprofileinput') || m.includes('createtherapistprofileinput');
  if (!mentionsInput) return false;
  return (
    m.includes('not defined for input object type') ||
    m.includes('variables input contains a field that is not defined') ||
    (message.includes('FieldUndefined') && m.includes('input'))
  );
}

/** Saved professional profile (TherapistProfile model) — parent-facing when visibility allows */
export type TherapistProfilePublic = {
  id?: string | null;
  userId?: string | null;
  headline?: string | null;
  bio?: string | null;
  approach?: string | null;
  specialties?: string[] | null;
  therapies?: string[] | null;
  ageRanges?: string[] | null;
  conditions?: string[] | null;
  experienceYears?: number | null;
  languages?: string[] | null;
  locations?: string[] | null;
  visibility?: string | null;
  linkedinUrl?: string | null;
  websiteUrl?: string | null;
  instagramUrl?: string | null;
  youtubeUrl?: string | null;
  videoIntroUrl?: string | null;
  /** S3 key for profile photo (public/ path) */
  profilePhotoKey?: string | null;
  acceptsNewClients?: boolean | null;
  sessionTypes?: string[] | null;
  sessionRateDisplay?: string | null;
  insurancePanels?: string[] | null;
  blogSlug?: string | null;
  lastUpdated?: string | null;
  education?: unknown;
  certifications?: unknown;
  awards?: unknown;
};

/** Matches current `amplify/data/resource.ts` — needs deployed schema in sync. */
const LIST_BY_USER_QUERY_FULL = `
  query ListTherapistProfileForUser($userId: ID!) {
    listTherapistProfiles(filter: { userId: { eq: $userId } }, limit: 1) {
      items {
        id
        userId
        headline
        bio
        approach
        specialties
        therapies
        ageRanges
        conditions
        experienceYears
        languages
        locations
        visibility
        linkedinUrl
        websiteUrl
        instagramUrl
        youtubeUrl
        videoIntroUrl
        profilePhotoKey
        acceptsNewClients
        sessionTypes
        sessionRateDisplay
        insurancePanels
        blogSlug
        lastUpdated
        education
        certifications
        awards
      }
    }
  }
`;

/** For AppSync APIs deployed before profilePhotoKey / sessionRateDisplay existed. */
const LIST_BY_USER_QUERY_LEGACY = `
  query ListTherapistProfileForUserLegacy($userId: ID!) {
    listTherapistProfiles(filter: { userId: { eq: $userId } }, limit: 1) {
      items {
        id
        userId
        headline
        bio
        approach
        specialties
        therapies
        ageRanges
        conditions
        experienceYears
        languages
        locations
        visibility
        linkedinUrl
        websiteUrl
        instagramUrl
        youtubeUrl
        videoIntroUrl
        acceptsNewClients
        sessionTypes
        insurancePanels
        blogSlug
        lastUpdated
        education
        certifications
        awards
      }
    }
  }
`;

/**
 * Load profile via public GraphQL (API key). Retries with a smaller selection set if the
 * deployed schema is missing newer fields.
 */
export async function loadTherapistProfileByUserIdGraphql(
  userId: string,
): Promise<TherapistProfilePublic | null> {
  if (!userId) return null;
  try {
    const data = await appsyncPublicGraphqlRequest<{
      listTherapistProfiles?: { items?: TherapistProfilePublic[] };
    }>(LIST_BY_USER_QUERY_FULL, { userId });
    return data?.listTherapistProfiles?.items?.[0] ?? null;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (!isStaleTherapistProfileSchemaError(msg)) throw e;
    const data = await appsyncPublicGraphqlRequest<{
      listTherapistProfiles?: { items?: TherapistProfilePublic[] };
    }>(LIST_BY_USER_QUERY_LEGACY, { userId });
    return data?.listTherapistProfiles?.items?.[0] ?? null;
  }
}

/** When false, parents should not see TherapistProfile fields (still may see User / verification). */
export function shouldExposeTherapistProfileToParents(visibility: string | null | undefined): boolean {
  if (visibility === 'PRIVATE') return false;
  return true;
}

/**
 * Load TherapistProfile row for marketplace / public profile page.
 * Fails soft (null) on error so listings still work.
 */
export async function fetchTherapistProfileByUserId(userId: string): Promise<TherapistProfilePublic | null> {
  if (!userId) return null;
  try {
    return await loadTherapistProfileByUserIdGraphql(userId);
  } catch {
    return null;
  }
}
